/* api.ts — the ONLY module in this bundle that makes a network request.
 *
 * THE RULE, IN CODE. The browser talks to the Fastify API and to nothing else. No vendor call, no
 * API key, ever. Anything in a frontend bundle is public — a key shipped to the browser is a key
 * published — so Anthropic, and every third-party vendor added later, is reached through the
 * backend.
 *
 * Every request in this app goes through this one function, which means the rule is checkable by
 * reading a single file rather than by trusting 4,000 lines. CI also greps the build output for
 * key-shaped strings and for any hostname that is not this app's own origin, and the page ships
 * `Content-Security-Policy: connect-src 'self'` so the browser enforces it too.
 */

export interface ApiError { error: string }

/* Relative, always. A relative URL cannot reach another origin, which makes the rule above
   structural rather than a matter of discipline. */
const BASE = '/api/';

export async function api<T>(path: string, init?: RequestInit): Promise<T | ApiError> {
  try {
    const res = await fetch(BASE + path, init);
    let body: unknown = null;
    try { body = await res.json(); } catch { /* not JSON — the status check below answers instead */ }
    if (body && typeof body === 'object' && 'error' in body) return body as ApiError;
    if (!res.ok) return { error: `the server answered HTTP ${res.status}` };
    return (body ?? { error: 'the server sent an empty reply' }) as T;
  } catch (e) {
    return { error: String((e as Error)?.message || e) };
  }
}

export const isErr = (v: unknown): v is ApiError =>
  !!v && typeof v === 'object' && 'error' in (v as Record<string, unknown>);

/* The two endpoints that write state or spend money are POST-only on the server. As GETs they were
   CSRF-able from any page with a bare <img src=…> — loopback binding does not stop a browser
   sending the request. Params stay on the query string, so a caller only adds the method. */
export const post = <T>(path: string) => api<T>(path, { method: 'POST' });

// ---------------------------------------------------------------- response shapes
// Mirrors of what the API returns. Kept narrow on purpose: these are the fields the UI reads, and a
// field appearing here that the API does not send is a contract question, not a typing convenience.

export type Entity = 'ppl' | 'acct' | 'fac';
export type FieldType = 'text' | 'num' | 'enum' | 'bool';

export interface Field { k: string; t: FieldType; lbl: string; w: number; src: string; grp: string }

export interface Meta {
  stats: Record<string, number>;
  fields: Record<Entity, Field[]>;
  opsFor: Record<FieldType, string[]>;
  derived: Record<Entity, string[]>;
  enums: Record<string, { v: string; n: number }[]>;
  keycol: Record<Entity, string>;
  states: { state: string; plants: number }[];
  gmr: unknown;
}

export type Row = Record<string, unknown> & { _key: string; _tags: string[]; _man: string[] };

export interface QueryResult {
  rows: Row[];
  agg: Record<string, number> | null;
  lists: Record<string, number>;
  ms: number;
  /* The compiled WHERE clause, shown as a receipt so a rep can see what actually ran. Its TEXT
     legitimately differs between the SQLite app and this one ($1 vs ?, ILIKE vs LIKE) — the meaning
     is the contract, not the string. */
  sql: string;
  entity: Entity; sort: string; dir: string; off: number; lim: number;
}

export interface TagCounts { entity: Entity; counts: Record<string, number>; ms: number }

/* The Ask bar. `status` rides along on every answer so the spend on screen is never stale — the
   thing being reported is money, and a stale budget is the one number nobody should have to
   refresh to trust. */
export interface AskStatus {
  enabled: boolean; reason: string | null; model: string;
  capUsd: number; spentUsd: number; remainingUsd: number;
  capDayUsd: number; spentDayUsd: number; remainingDayUsd: number;
  callsHour: number; callsDay: number; capped: boolean;
}

export interface AskResult {
  entity: Entity;
  ast: { op: 'AND'; kids: unknown[] };
  /* What the model says it understood, and — the honest half — what it could not map. `dropped` is
     what the SERVER refused after validating against the registry, which is a different and more
     trustworthy list than the model's own self-report. Both are shown. */
  read: string;
  ignored: string;
  dropped: string[];
  sort: string | null;
  dir: 'asc' | 'desc';
  by: 'claude';
  model: string;
  costUsd: number;
  status: AskStatus;
}
